import { HeaderPositionType } from "../constants";
import { BaseCategory } from "./BaseCategory";
import { SlideCategories } from "./CategoryController";
import { SlideModelBuilder } from "./SlideModelBuilder";

export class HeatMapCategory extends BaseCategory {
    get name() {
        return "Heat Map";
    }

    get type() {
        return SlideCategories.HEAT_MAP;
    }

    get icon() {
        return null;
    }

    get description() {
        return "The content represents data that should be visualized as a heatmap with color-coded cells. Use this for correlation matrices, performance grids, comparison tables, or any data where color intensity represents values or categories.";
    }

    get slideTypes() {
        return [];
    }

    getPrompt() {
        return `Create a heat map table where cells are color-coded based on their values or categories.
        Provide row and column labels, and cell data with intensity values.
        Intensity values should range from 0 (low/green) to 100 (high/red), transitioning through yellow at 50.
        Optionally specify cell colors for categorical data.`;
    }

    getImagePrompt() {
        return false;
    }

    getPromptSchema() {
        return {
            rowLabels: ["array of row label strings"],
            colLabels: ["array of column label strings"],
            cells: [{
                row: "zero-based row index",
                col: "zero-based column index",
                value: "the display value (text or number)",
                intensity: "numeric value 0-100 representing heat (0=green/low, 50=yellow/medium, 100=red/high)",
                color: "optional: CSS color string (e.g., 'rgb(255,0,0)', '#ff0000') or color name - if omitted, uses intensity-based green-to-red gradient"
            }],
            conclusion: "an optional short conclusion or takeaway for the slide",
        };
    }

    validateGeneratedModel(model) {
        if (!this.validateArray(model.cells)) {
            return false;
        }
        return true;
    }

    getSlideModels(model, options = {}) {
        let models = [
            this.buildHeatMap(model, options)
        ];
        return models;
    }

    getCellColorFromIntensity(intensity) {
        // Map intensity (0-100) to color gradient from green to red
        // 0-50: green (0,255,0) -> yellow (255,255,0)
        // 50-100: yellow (255,255,0) -> red (255,0,0)

        // Clamp intensity to 0-100 range
        intensity = Math.max(0, Math.min(100, intensity));

        let r, g, b;

        if (intensity <= 50) {
            // Green to Yellow
            const t = intensity / 50; // 0 to 1
            r = Math.round(255 * t);
            g = 255;
            b = 0;
        } else {
            // Yellow to Red
            const t = (intensity - 50) / 50; // 0 to 1
            r = 255;
            g = Math.round(255 * (1 - t));
            b = 0;
        }

        return `rgb(${r}, ${g}, ${b})`;
    }

    buildHeatMap(model, options = {}) {
        let builder = new SlideModelBuilder("dashboard", model);
        builder.createHeader({ heading: model.slideTitle, position: HeaderPositionType.TOP });

        const rowCount = model.rowLabels?.length || 0;
        const colCount = model.colLabels?.length || 0;

        // Build rows - first row is header, rest are data rows
        const rows = [
            { index: 0, style: "headerRow" }
        ];
        for (let i = 0; i < rowCount; i++) {
            rows.push({ index: i + 1, style: "defaultRow" });
        }

        // Build cols - first col is header (row labels), rest are data cols
        const cols = [
            { index: 0, style: "headerCol" }
        ];
        for (let i = 0; i < colCount; i++) {
            cols.push({ index: i + 1, style: "defaultCol" });
        }

        // Build cells
        const cells = [];

        // Top-left corner cell (empty or title)
        cells.push({
            row: 0,
            col: 0,
            cellText: { text: "" }
        });

        // Column header cells
        model.colLabels?.forEach((label, index) => {
            cells.push({
                row: 0,
                col: index + 1,
                cellText: { text: label }
            });
        });

        // Row label cells
        model.rowLabels?.forEach((label, index) => {
            cells.push({
                row: index + 1,
                col: 0,
                cellText: { text: label }
            });
        });

        // Data cells with heat map colors
        model.cells?.forEach(cell => {
            const cellColor = cell.color || this.getCellColorFromIntensity(cell.intensity || 0);

            cells.push({
                row: cell.row + 1, // +1 because first row is header
                col: cell.col + 1, // +1 because first col is row labels
                cellText: { text: String(cell.value) },
                cellStyle: "fill",
                cellColor: cellColor
            });
        });

        builder.createPrimary({
            items: [{
                componentType: "Table",
                childElement: {
                    rows: rows,
                    cols: cols,
                    cells: cells,
                }
            }],
        });

        if (options.citeSources) {
            builder.createSourcesAttribution(model.sources);
        }

        if (model.conclusion) {
            builder.createBottomTray(model.conclusion);
        }

        return builder.model;
    }
}
